import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Add from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getVendor,
  listItems,
  createVendorItem,
} from '../../../endpoints/chapterSupplies';
import type { CreateVendorItemRequest } from '../../../types/chapterSupplies';

export default function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemForm, setAddItemForm] = useState<CreateVendorItemRequest>({
    item_id: '',
    vendor_sku: null,
    cost: null,
    lead_time_days: null,
    is_preferred: 0,
  });
  const [addItemError, setAddItemError] = useState<string | null>(null);

  const { data: vendor, isFetching } = useApiGet(
    ['chapter-supplies-vendor', id],
    () => getVendor(id!),
    { enabled: !!id, retry: 1 }
  );

  const { data: itemsData } = useApiGet(
    ['chapter-supplies-items-for-vendor', 200],
    () => listItems({ limit: 200, offset: 0 }),
    { enabled: addItemOpen, retry: 1 }
  );

  const items = itemsData?.items ?? [];
  const vendorItemIds = (vendor?.vendor_items ?? []).map((vi) => vi.item_id);
  const itemOptions = items.filter((i) => !vendorItemIds.includes(i.id));

  const { mutate: submitVendorItem, isPending: isAddingItem } = useApiSend(
    (params: { vendorId: string; data: CreateVendorItemRequest }) =>
      createVendorItem(params.vendorId, params.data),
    () => {
      setAddItemOpen(false);
      setAddItemForm({
        item_id: '',
        vendor_sku: null,
        cost: null,
        lead_time_days: null,
        is_preferred: 0,
      });
      setAddItemError(null);
    },
    (err) => {
      setAddItemError(err?.message ?? 'Failed to add vendor item');
    },
    [['chapter-supplies-vendor', id]]
  );

  const handleOpenAddItem = () => {
    setAddItemError(null);
    setAddItemForm({
      item_id: '',
      vendor_sku: null,
      cost: null,
      lead_time_days: null,
      is_preferred: 0,
    });
    setAddItemOpen(true);
  };

  const handleCloseAddItem = () => {
    setAddItemOpen(false);
    setAddItemError(null);
  };

  const handleAddItemSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !addItemForm.item_id?.trim()) {
      setAddItemError('Item is required');
      return;
    }
    submitVendorItem({
      vendorId: id,
      data: {
        item_id: addItemForm.item_id.trim(),
        vendor_sku: addItemForm.vendor_sku?.trim() || null,
        cost: addItemForm.cost != null ? Number(addItemForm.cost) : null,
        lead_time_days:
          addItemForm.lead_time_days != null
            ? Number(addItemForm.lead_time_days)
            : null,
        is_preferred: addItemForm.is_preferred ? 1 : 0,
      },
    });
  };

  if (!id) {
    return (
      <Grid container paddingLeft={2} paddingRight={2}>
        <Typography>Invalid vendor ID.</Typography>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title={vendor?.name ?? 'Vendor'}
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management/vendors')}
            >
              Back to list
            </Button>
          }
          rightDom={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddItem}
            >
              Add item
            </Button>
          }
          subtitle={undefined}
        />
      </Grid>
      {isFetching && (
        <Grid item xs={12}>
          <Typography>Loading…</Typography>
        </Grid>
      )}
      {!isFetching && vendor && (
        <>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Contact &amp; address
            </Typography>
            <Typography variant="body1">
              {vendor.contact_name && (
                <>
                  <strong>Contact:</strong> {vendor.contact_name}
                  <br />
                </>
              )}
              {vendor.contact_email && (
                <>
                  <strong>Email:</strong> {vendor.contact_email}
                  <br />
                </>
              )}
              {vendor.contact_phone && (
                <>
                  <strong>Phone:</strong> {vendor.contact_phone}
                  <br />
                </>
              )}
              {(vendor.address_line1 || vendor.city) && (
                <>
                  <strong>Address:</strong>{' '}
                  {[
                    vendor.address_line1,
                    vendor.address_line2,
                    vendor.city,
                    vendor.state_name ?? vendor.state_id,
                    vendor.postal_code,
                    vendor.country_name ?? vendor.country_id,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  <br />
                </>
              )}
              {vendor.notes && (
                <>
                  <strong>Notes:</strong> {vendor.notes}
                </>
              )}
              {!vendor.contact_name &&
                !vendor.contact_email &&
                !vendor.contact_phone &&
                !vendor.address_line1 &&
                !vendor.city &&
                !vendor.notes && (
                  <Typography color="text.secondary">
                    No contact or address details.
                  </Typography>
                )}
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Vendor items
            </Typography>
            {(vendor.vendor_items ?? []).length === 0 ? (
              <Typography color="text.secondary">
                No items linked. Use &quot;Add item&quot; to link an item from
                the catalog.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Vendor SKU</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell align="right">Lead time (days)</TableCell>
                      <TableCell>Preferred</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vendor.vendor_items ?? []).map((vi) => (
                      <TableRow key={vi.id}>
                        <TableCell>{vi.item_name ?? vi.item_id}</TableCell>
                        <TableCell>{vi.vendor_sku ?? '—'}</TableCell>
                        <TableCell align="right">
                          {vi.cost != null ? vi.cost : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {vi.lead_time_days != null ? vi.lead_time_days : '—'}
                        </TableCell>
                        <TableCell>{vi.is_preferred ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </>
      )}

      <Dialog
        open={addItemOpen}
        onClose={handleCloseAddItem}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleAddItemSubmit}>
          <DialogTitle>Add vendor item</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={itemOptions}
                  getOptionLabel={(option) => option.name ?? option.id}
                  value={
                    items.find((i) => i.id === addItemForm.item_id) ?? null
                  }
                  onChange={(_, value) =>
                    setAddItemForm((prev) => ({
                      ...prev,
                      item_id: value?.id ?? '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item"
                      required
                      error={!!addItemError && !addItemForm.item_id}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Vendor SKU"
                  value={addItemForm.vendor_sku ?? ''}
                  onChange={(e) =>
                    setAddItemForm((prev) => ({
                      ...prev,
                      vendor_sku: e.target.value || null,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  label="Cost"
                  value={addItemForm.cost ?? ''}
                  onChange={(e) =>
                    setAddItemForm((prev) => ({
                      ...prev,
                      cost:
                        e.target.value === ''
                          ? null
                          : parseFloat(e.target.value),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                  label="Lead time (days)"
                  value={addItemForm.lead_time_days ?? ''}
                  onChange={(e) =>
                    setAddItemForm((prev) => ({
                      ...prev,
                      lead_time_days:
                        e.target.value === ''
                          ? null
                          : parseInt(e.target.value, 10),
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!addItemForm.is_preferred}
                      onChange={(e) =>
                        setAddItemForm((prev) => ({
                          ...prev,
                          is_preferred: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label="Preferred"
                />
              </Grid>
              {addItemError && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {addItemError}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddItem}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isAddingItem || !addItemForm.item_id?.trim()}
            >
              {isAddingItem ? 'Adding…' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Grid>
  );
}
